import React from 'react';

// helpers
import { styled } from 'styled-components';
import { colorsTheme } from 'resources/theme/styled/colors';
import { FormValuesModel } from '.';
import { useFormikContext } from 'formik';
import { DISABLED_FORM_STATUS } from 'constants/form';

// components
import InfoTooltip from 'components/Tooltips/InfoTooltip';
import LoadingWrapper from 'components/WrapperComponents/LoadingWrapper';
import EllipsisTooltip from 'components/Tooltips/EllipsisTooltip';
import { Col, IconSVG, Row, Text } from '@ui';
import { ReactComponent as BlankCheckIcon } from 'resources/icons/remix-icons/checkbox-blank-line.svg';
import { ReactComponent as FilledCheckIcon } from 'resources/icons/remix-icons/checkbox-line.svg';

export type EntityTypeOption = {
  id: string;
  label: string;
  description: string;
};

export interface IProps {
  loading?: boolean;
  options: EntityTypeOption[];
}

const Option = ({
  text,
  tooltipText,
  isActive,
  onSelect,
  disabled,
}: {
  text: string;
  tooltipText: string;
  isActive: boolean;
  onSelect: () => void;
  disabled?: boolean;
}) => {
  return (
    <InfoTooltip tooltipContent={tooltipText || undefined}>
      <StyledOptionRoot
        $isActive={isActive}
        onClick={onSelect}
        $disabled={disabled}
      >
        <Row wrap={false} align="middle">
          <Col flex="50px">
            <CenterAlignWrapper>
              <IconSVG
                size="middle"
                color={colorsTheme.colorWhite}
                component={isActive ? FilledCheckIcon : BlankCheckIcon}
              />
            </CenterAlignWrapper>
          </Col>
          <Col flex="auto">
            <Text
              variant="h4"
              color={isActive ? colorsTheme.colorWhite : undefined}
            >
              <EllipsisTooltip title={text}>{text}</EllipsisTooltip>
            </Text>
          </Col>
        </Row>
      </StyledOptionRoot>
    </InfoTooltip>
  );
};

const AvailableEntityTypes = ({ loading, options }: IProps) => {
  const { values, setFieldValue } = useFormikContext<FormValuesModel>();
  const { status } = useFormikContext();
  const handleEntityTypeCategorySelect = (newValue: string) => {
    setFieldValue('entityTypeCategory', newValue);
  };

  const isDisabled = status === DISABLED_FORM_STATUS;

  return (
    <LoadingWrapper loading={!!loading}>
      <Row gutter={[16, 16]}>
        {options.map((e) => (
          <Col span={24} key={e.id}>
            <Option
              disabled={status === DISABLED_FORM_STATUS}
              text={e.label}
              tooltipText={e.description}
              isActive={values.entityTypeCategory === e.id}
              onSelect={() =>
                !isDisabled && handleEntityTypeCategorySelect(e.id)
              }
            />
          </Col>
        ))}
      </Row>
    </LoadingWrapper>
  );
};

const StyledOptionRoot = styled.div<{
  $isActive: boolean;
  $disabled?: boolean;
}>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  padding: ${({ theme }) => theme.paddingXl};
  border: ${({ theme }) => `2px solid ${theme.colorDarkL3}`};
  transition: ${({ theme }) => theme.defaultTransition};
  &:hover {
    border-color: ${({ theme, $disabled }) =>
      !$disabled ? theme.colorWhite : 'none'};
  }
  ${({ $isActive, theme }) =>
    $isActive ? `border-color: ${theme.colorWhite};` : ''}
`;

const CenterAlignWrapper = styled.div`
  display: flex;
  align-items: left;
  justify-content: left;
`;

export default AvailableEntityTypes;
